import { useState, useEffect, useRef } from 'react';

interface LoadingState {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
  withLoading: <T>(asyncFunction: () => Promise<T>, delay?: number) => Promise<T>;
}

export const useLoading = (delay: number = 400): LoadingState => {
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingStartTimeRef = useRef<number | null>(null);

  const startLoading = () => {
    loadingStartTimeRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      setIsLoading(true);
    }, delay);
  };

  const stopLoading = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLoading(false);
    loadingStartTimeRef.current = null;
  };

  const withLoading = async <T>(
    asyncFunction: () => Promise<T>,
    customDelay?: number
  ): Promise<T> => {
    const actualDelay = customDelay ?? delay;
    startLoading();
    
    try {
      const result = await asyncFunction();
      return result;
    } finally {
      // Solo ocultar el loading si ha pasado el tiempo mínimo
      const elapsedTime = Date.now() - (loadingStartTimeRef.current || 0);
      if (elapsedTime < actualDelay) {
        // Si la operación terminó antes del delay, esperar el tiempo restante
        setTimeout(() => {
          stopLoading();
        }, actualDelay - elapsedTime);
      } else {
        stopLoading();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isLoading,
    startLoading,
    stopLoading,
    withLoading
  };
};
